/**
 * A mensagem como o WhatsApp a mostra — PURO de propósito: sem React, sem alias
 * `@/`. Mora no @erp/shared porque são DOIS leitores: a tela (whatsapp-text.tsx)
 * e a conversa em PDF que a API gera (chat-transcript-document.ts). Uma cópia em
 * cada lado divergiria justamente no que a pessoa lê. A prova mora na API
 * (apps/api/src/shared/whatsapp-format.test.ts), como a espera da mídia.
 *
 * Até aqui a conversa do ERP mostrava o texto cru: `*TESTE*` com os asteriscos,
 * e a fala do bot com os botões colados no fim (`[Tudo certo 👍] [Alterar
 * Dados]`). Duas leituras, as duas conservadoras — na dúvida, o texto fica
 * como veio:
 *
 * - `parseWhatsappFormatting`: `*negrito*`, `_itálico_`, `~riscado~`,
 *   `` `mono` `` e ```` ```bloco``` ````, numa ÁRVORE de nós. Quem desenha
 *   (whatsapp-text.tsx) monta elementos React a partir dela — o texto entra
 *   sempre como texto, nunca como HTML;
 * - `splitBotOptions`: as opções (botões, itens de lista) que a Zeepchat cola
 *   na ÚLTIMA linha da fala do bot. O formato é o do manager
 *   (flow-engine/transcript.ts, `withChoices`): `${corpo}\n[a] [b]`. O evento
 *   zeep.flow.message só traz o texto — não há payload estruturado guardado.
 */

export type WaNode =
  | { kind: 'text'; text: string }
  | { kind: 'bold' | 'italic' | 'strike'; children: WaNode[] }
  | { kind: 'mono'; text: string };

/**
 * URL http(s) no texto — a MESMA que o LinkifiedText transforma em link. O que
 * está dentro dela nunca abre nem fecha marca: `https://x.com/_a_` continua um
 * link inteiro, não um link quebrado com "a" em itálico.
 */
export const WA_URL_RE = /(https?:\/\/[^\s<>"')\]]+)/g;

const STYLE_BY_MARKER: Record<string, 'bold' | 'italic' | 'strike'> = {
  '*': 'bold',
  _: 'italic',
  '~': 'strike',
};

/** além disto o que estiver dentro fica como texto (marca dentro de marca dentro de…) */
const MAX_DEPTH = 3;

const isSpace = (ch: string | undefined) => ch !== undefined && /\s/.test(ch);
/** letra ou dígito (com acento): marca colada numa palavra não é marca — `2*3*4`, `nome_do_campo` */
const isWordChar = (ch: string | undefined) => ch !== undefined && /[\p{L}\p{N}]/u.test(ch);

/** máscara das posições que estão dentro de uma URL */
function urlMask(text: string): { inside: boolean[]; starts: Map<number, number> } {
  const inside = new Array<boolean>(text.length).fill(false);
  const starts = new Map<number, number>();
  for (const m of text.matchAll(WA_URL_RE)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    starts.set(start, end);
    for (let k = start; k < end; k++) inside[k] = true;
  }
  return { inside, starts };
}

/**
 * Abre marca em `i`? O próximo caractere existe e não é espaço nem a mesma
 * marca (`**` vazio é texto), e o anterior não é letra nem dígito.
 */
function opensAt(text: string, i: number): boolean {
  const next = text[i + 1];
  if (next === undefined || isSpace(next) || next === text[i]) return false;
  return !isWordChar(text[i - 1]);
}

/**
 * Onde fecha a marca aberta em `i`: a mesma marca, na MESMA linha, com conteúdo
 * não vazio, sem espaço antes dela e sem letra ou dígito depois. -1 = não fecha
 * (a marca fica como texto).
 */
function closingIndex(text: string, i: number, inside: boolean[]): number {
  const marker = text[i];
  for (let j = i + 2; j < text.length; j++) {
    const ch = text[j];
    if (ch === '\n') return -1;
    if (inside[j] || ch !== marker) continue;
    if (isSpace(text[j - 1])) continue;
    if (isWordChar(text[j + 1])) continue;
    return j;
  }
  return -1;
}

function parse(text: string, depth: number): WaNode[] {
  const nodes: WaNode[] = [];
  const { inside, starts } = urlMask(text);
  let buffer = '';
  const flush = () => {
    if (buffer !== '') nodes.push({ kind: 'text', text: buffer });
    buffer = '';
  };

  let i = 0;
  while (i < text.length) {
    // a URL passa inteira, como texto (o link sai no desenho)
    const urlEnd = starts.get(i);
    if (urlEnd !== undefined) {
      buffer += text.slice(i, urlEnd);
      i = urlEnd;
      continue;
    }
    // ```bloco``` — monoespaçado, pode ter quebra de linha, conteúdo literal
    if (text.startsWith('```', i)) {
      const end = text.indexOf('```', i + 3);
      if (end > i + 3) {
        flush();
        nodes.push({ kind: 'mono', text: text.slice(i + 3, end) });
        i = end + 3;
        continue;
      }
    }
    const ch = text[i]!;
    const style = STYLE_BY_MARKER[ch];
    if ((style !== undefined || ch === '`') && opensAt(text, i)) {
      const close = closingIndex(text, i, inside);
      if (close !== -1) {
        flush();
        const inner = text.slice(i + 1, close);
        if (style === undefined) nodes.push({ kind: 'mono', text: inner });
        else {
          nodes.push({
            kind: style,
            children: depth < MAX_DEPTH ? parse(inner, depth + 1) : [{ kind: 'text', text: inner }],
          });
        }
        i = close + 1;
        continue;
      }
    }
    buffer += ch;
    i++;
  }
  flush();
  return nodes;
}

/** A árvore da formatação do WhatsApp. Marca sem par fica como texto. */
export function parseWhatsappFormatting(text: string): WaNode[] {
  return parse(text, 0);
}

/** o texto da árvore, sem as marcas — o que a pessoa lê */
export function waPlainText(nodes: WaNode[]): string {
  return nodes
    .map((n) => (n.kind === 'text' || n.kind === 'mono' ? n.text : waPlainText(n.children)))
    .join('');
}

// ── as opções da fala do bot ────────────────────────────────────────────────

/** a lista do WhatsApp tem até 10 itens (botões, até 3) — mais que isso não é opção */
export const BOT_OPTIONS_MAX = 10;
/** título de botão tem 20 caracteres, item de lista 24 — folga, mas não um parágrafo */
export const BOT_OPTION_TITLE_MAX = 60;

const OPTIONS_LINE_RE = /^\[[^[\]\n]+\](?: +\[[^[\]\n]+\])*$/;
const OPTION_RE = /\[([^[\]\n]+)\]/g;

/**
 * Separa as opções que o manager colou na última linha (`[a] [b]`) do corpo da
 * mensagem. Só quando a ÚLTIMA linha é feita SÓ de opções, depois de uma
 * quebra de linha — `[imagem]` sozinho, `[localização] Rua X` e colchete no
 * meio do texto ficam como vieram. Nada reconhecido: `options` vazio e `body`
 * é o texto inteiro.
 */
export function splitBotOptions(text: string): { body: string; options: string[] } {
  const none = { body: text, options: [] as string[] };
  const trimmed = text.replace(/\s+$/, '');
  const nl = trimmed.lastIndexOf('\n');
  if (nl === -1) return none;
  const line = trimmed.slice(nl + 1).trim();
  if (!OPTIONS_LINE_RE.test(line)) return none;
  const options = [...line.matchAll(OPTION_RE)].map((m) => m[1]!.trim());
  if (options.length > BOT_OPTIONS_MAX) return none;
  if (options.some((o) => o === '' || o.length > BOT_OPTION_TITLE_MAX)) return none;
  return { body: trimmed.slice(0, nl).replace(/\s+$/, ''), options };
}
